import { SagaNodeKind } from '@/dtos/SagaWorkflow'
import { Position, type Edge } from '@xyflow/react'
import type { StateFlowNode } from './nodeTypes'

export const NODE_WIDTH = 168
export const NODE_HEIGHT = 52

// Strides are the node box plus the inter-node gap; the vertical gap leaves room for a self-loop arc above a node.
const COL_STRIDE = NODE_WIDTH + 72
const LANE_STRIDE = NODE_HEIGHT + 40

const TERMINAL_KINDS = SagaNodeKind.TerminalOk | SagaNodeKind.TerminalFail

const kindOf = (n: StateFlowNode): number => n.data.kind

/**
 * Deterministic layered layout — a pure function of the graph topology, so a definition always renders identically.
 * Columns are the SHORTEST-hop distance from the entry node(s), so wait-states reachable directly from the entry
 * collapse into one column as sibling alternatives. Terminals are pinned to the last column; lane order within a column
 * follows the server's declaration order (happy path on top). Self-loops carry no position. Returns the positioned
 * nodes plus each node's column index (used to style same-column / multi-column transitions vs the adjacent backbone).
 */
export const layoutNodes = (nodes: StateFlowNode[], edges: Edge[]): { nodes: StateFlowNode[]; colById: Map<string, number> } => {
  const ids = nodes.map(n => n.id)
  const forward = edges.filter(e => e.source !== e.target)

  const succ = new Map<string, string[]>(ids.map(id => [id, []]))
  const indeg = new Map<string, number>(ids.map(id => [id, 0]))
  forward.forEach(e => {
    if (!succ.has(e.source) || !indeg.has(e.target)) return
    succ.get(e.source)!.push(e.target)
    indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1)
  })

  // Shortest-path rank via Kahn topological order: each node's column is its FEWEST hops from an entry node. Entry
  // nodes (no incoming edge) seed at 0; everything else starts at Infinity and is relaxed downward as its predecessors
  // are finalized in topological order. Any node still Infinity after the sweep (cycle remnant / unreachable) falls
  // back to 0 — defensive, never loops forever.
  const col = new Map<string, number>(ids.map(id => [id, (indeg.get(id) ?? 0) === 0 ? 0 : Infinity]))
  const remaining = new Map(indeg)
  const ready = ids.filter(id => (remaining.get(id) ?? 0) === 0)
  while (ready.length > 0) {
    const u = ready.shift()!
    const cu = col.get(u) ?? 0
    for (const v of succ.get(u) ?? []) {
      col.set(v, Math.min(col.get(v) ?? Infinity, cu + 1))
      remaining.set(v, (remaining.get(v) ?? 0) - 1)
      if ((remaining.get(v) ?? 0) === 0) ready.push(v)
    }
  }
  ids.forEach(id => { if (!Number.isFinite(col.get(id))) col.set(id, 0) })

  let maxCol = 0
  col.forEach(c => { maxCol = Math.max(maxCol, c) })

  // Pin every terminal to the last column so RESOLVED / DISCARDED / *_REJECTED always share the rightmost lane stack.
  nodes.forEach(n => { if ((kindOf(n) & TERMINAL_KINDS) !== 0) col.set(n.id, maxCol) })

  // Lane order within a column = first-appearance order across [nodes…, then each edge's endpoints]. Deterministic
  // because the server emits Nodes/Edges in the C# definition's array order (happy path first → success lane on top).
  const appearance = new Map<string, number>()
  let seq = 0
  const see = (id: string) => { if (!appearance.has(id)) appearance.set(id, seq++) }
  ids.forEach(see)
  edges.forEach(e => { see(e.source); see(e.target) })

  const lanesByCol = new Map<number, string[]>()
  ids.forEach(id => {
    const c = col.get(id) ?? 0
    const bucket = lanesByCol.get(c) ?? []
    bucket.push(id)
    lanesByCol.set(c, bucket)
  })
  const lane = new Map<string, number>()
  lanesByCol.forEach(bucket => {
    bucket.sort((a, b) => (appearance.get(a) ?? 0) - (appearance.get(b) ?? 0))
    bucket.forEach((id, i) => lane.set(id, i))
  })

  const laidOut = nodes.map(n => {
    const c = col.get(n.id) ?? 0
    const count = lanesByCol.get(c)?.length ?? 1
    const i = lane.get(n.id) ?? 0
    const centerY = i * LANE_STRIDE - ((count - 1) * LANE_STRIDE) / 2
    return {
      ...n,
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      position: { x: c * COL_STRIDE, y: centerY - NODE_HEIGHT / 2 },
    }
  })

  return { nodes: laidOut, colById: col }
}
